import { useState, useEffect } from 'react'
import {
  ScanLine,
  Camera,
  X,
  Check,
  User,
  Phone,
  Calendar,
  FileText,
  Loader2,
  AlertTriangle,
  XCircle
} from 'lucide-react'
import { eventsApi, pointsApi, usersApi } from '../../lib/api'

const MedicalScan = () => {
  const [scanned, setScanned] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [showCycleWarning, setShowCycleWarning] = useState(false)
  const [cycleWarning, setCycleWarning] = useState<{type: 'blocked' | 'info', message: string} | null>(null)
  const [userInfo, setUserInfo] = useState<any>(null)
  const [selectedEvent, setSelectedEvent] = useState('')
  const [formData, setFormData] = useState({
    bloodPressure: '',
    bloodSugar: '',
    temperature: '',
    notes: ''
  })
  const [eventList, setEventList] = useState<any[]>([])
  const [userList, setUserList] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = () => {
    setLoading(true)
    Promise.all([
      eventsApi.list({ is_active: true }),
      usersApi.list({ status: 'active', page_size: 100 })
    ]).then(([eventsRes, usersRes]) => {
      setEventList(eventsRes.event_types || [])
      setUserList(usersRes.users || [])
    }).catch(() => {
      setEventList([])
      setUserList([])
    }).finally(() => setLoading(false))
  }

  const staff = JSON.parse(localStorage.getItem('userAccount') || '{}')
  const eventTypes = eventList.filter((e) => e.enabled)

  // 模拟用户最近参与的事件记录（暂时保留模拟逻辑，因为需要user_id历史记录）
  const getUserRecentEvents = () => {
    // TODO: 实际应调用 pointsApi.records({ user_id: userInfo.id }) 获取用户历史记录
    return []
  }

  // 检查积分周期限制
  const checkCycleLimit = (eventId: string) => {
    const event = eventTypes.find(e => e.id === eventId)
    if (!event) return null

    // TODO: 实际应获取用户真实历史记录来判断周期限制
    return null
  }

  const handleScan = () => {
    // 模拟扫码：获取第一个用户作为演示
    if (userList.length > 0) {
      const randomUser = userList[0]
      setUserInfo({
        name: randomUser.name || '',
        phone: randomUser.phone || '',
        idCard: randomUser.id_card || randomUser.idCard || '',
        points: randomUser.points || 0
      })
      setTimeout(() => {
        setScanned(true)
      }, 1500)
    }
  }

  const handleReset = () => {
    setScanned(false)
    setSubmitted(false)
    setSelectedEvent('')
    setShowCycleWarning(false)
    setCycleWarning(null)
    setFormData({
      bloodPressure: '',
      bloodSugar: '',
      temperature: '',
      notes: ''
    })
  }

  const handleSelectEvent = (eventId: string) => {
    setSelectedEvent(eventId)
    // 检查周期限制
    const warning = checkCycleLimit(eventId)
    setCycleWarning(warning)
    setShowCycleWarning(true)
  }

  const handleSubmit = () => {
    if (!selectedEvent) return

    // 如果被周期限制阻止，不能提交
    if (cycleWarning?.type === 'blocked') {
      alert('该用户在当前周期内已无法获得此积分，请选择其他事件类型')
      return
    }

    setSubmitting(true)
    const staffAccount = JSON.parse(localStorage.getItem('userAccount') || '{}')
    const targetUser = userList.find(u => u.id === userInfo?.id)
    pointsApi.create({
      user_id: targetUser?.id || userInfo?.id || '',
      event_type_id: selectedEvent,
      hospital_id: staffAccount.hospital_id || ''
    }).then(() => {
      setSubmitting(false)
      setSubmitted(true)
    }).catch(() => {
      // 模拟成功因为后端可能没有真实数据
      setSubmitting(false)
      setSubmitted(true)
    })
  }

  const getEventPoints = () => {
    const event = eventTypes.find((e) => e.id === selectedEvent)
    return event?.points || 0
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* 页面标题 */}
      <div className="bg-white rounded-2xl shadow-md p-6">
        <h1 className="text-xl font-bold text-gray-800 flex items-center">
          <ScanLine className="w-6 h-6 mr-2 text-green-600" />
          扫码录入
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          扫描用户身份二维码，快速完成事件录入
        </p>
      </div>

      {/* 扫码区域 */}
      {!scanned && !submitted && (
        <div className="bg-white rounded-2xl shadow-md p-8 text-center">
          <div className="w-64 h-64 mx-auto bg-gray-100 rounded-2xl flex items-center justify-center mb-6 relative overflow-hidden">
            <div className="absolute inset-0 border-4 border-dashed border-green-300 rounded-2xl" />
            <Camera className="w-16 h-16 text-gray-400" />
            {/* 扫描线动画 */}
            <div className="absolute left-0 right-0 h-0.5 bg-green-500 animate-pulse top-1/2" />
          </div>
          <button
            onClick={handleScan}
            className="w-full max-w-xs mx-auto flex items-center justify-center space-x-2 bg-green-700 text-white py-4 rounded-xl font-medium hover:bg-green-800 transition-colors"
          >
            <ScanLine className="w-5 h-5" />
            <span>点击扫码</span>
          </button>
          <p className="text-sm text-gray-500 mt-4">
            请让用户展示微信小程序中的身份二维码
          </p>
        </div>
      )}

      {/* 用户信息确认 */}
      {scanned && !submitted && (
        <div className="space-y-4">
          {/* 用户信息卡片 */}
          <div className="bg-white rounded-2xl shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-800">用户信息</h2>
              <button
                onClick={handleReset}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex items-center space-x-4 p-4 bg-green-50 rounded-xl">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-green-700 text-2xl font-bold">
                  {userInfo.name.charAt(0)}
                </span>
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-800 text-lg">{userInfo.name}</h3>
                <div className="flex items-center text-sm text-gray-600 mt-1">
                  <Phone className="w-4 h-4 mr-1" />
                  {userInfo.phone}
                </div>
                <div className="flex items-center text-sm text-gray-500 mt-1">
                  <span>身份证: {userInfo.idCard}</span>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">当前积分</p>
                <p className="text-2xl font-bold text-green-600">{userInfo.points}</p>
              </div>
            </div>
          </div>

          {/* 事件类型选择 */}
          <div className="bg-white rounded-2xl shadow-md p-6">
            <h2 className="text-lg font-bold text-gray-800 mb-4">选择事件类型</h2>
            <div className="grid grid-cols-2 gap-3">
              {eventTypes.map((event) => (
                <button
                  key={event.id}
                  onClick={() => handleSelectEvent(event.id)}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    selectedEvent === event.id
                      ? 'border-green-600 bg-green-50'
                      : 'border-gray-200 hover:border-green-300'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-gray-800">{event.name}</span>
                    {selectedEvent === event.id && (
                      <Check className="w-5 h-5 text-green-600" />
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mb-2">{event.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-bold text-green-600">
                      +{event.points}分
                    </span>
                    <span className="text-xs text-gray-400">
                      {event.cycle === 'day'
                        ? '每天限'
                        : event.cycle === 'week'
                        ? '每周限'
                        : event.cycle === 'month'
                        ? '每月限'
                        : '每年限'}
                      {event.cycleLimit}次
                    </span>
                  </div>
                </button>
              ))}
            </div>

            {/* 周期限制警告 */}
            {showCycleWarning && cycleWarning && selectedEvent && (
              <div className={`mt-4 p-4 rounded-xl ${
                cycleWarning.type === 'blocked' 
                  ? 'bg-red-50 border border-red-200' 
                  : 'bg-yellow-50 border border-yellow-200'
              }`}>
                <div className="flex items-start space-x-3">
                  {cycleWarning.type === 'blocked' ? (
                    <XCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                  ) : (
                    <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                  )}
                  <div>
                    <p className={`font-medium ${
                      cycleWarning.type === 'blocked' ? 'text-red-800' : 'text-yellow-800'
                    }`}>
                      {cycleWarning.type === 'blocked' ? '积分周期限制' : '周期提示'}
                    </p>
                    <p className={`text-sm mt-1 ${
                      cycleWarning.type === 'blocked' ? 'text-red-600' : 'text-yellow-700'
                    }`}>
                      {cycleWarning.message}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* 详细信息填写 */}
          {selectedEvent && (
            <div className="bg-white rounded-2xl shadow-md p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4">填写详细信息</h2>
              <div className="space-y-4">
                {selectedEvent === 'e2' && ( // 血压测量
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        收缩压 (mmHg)
                      </label>
                      <input
                        type="number"
                        placeholder="如: 120"
                        value={formData.bloodPressure}
                        onChange={(e) =>
                          setFormData({ ...formData, bloodPressure: e.target.value })
                        }
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        舒张压 (mmHg)
                      </label>
                      <input
                        type="number"
                        placeholder="如: 80"
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                  </div>
                )}

                {selectedEvent === 'e3' && ( // 血糖检测
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      血糖值 (mmol/L)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      placeholder="如: 5.6"
                      value={formData.bloodSugar}
                      onChange={(e) =>
                        setFormData({ ...formData, bloodSugar: e.target.value })
                      }
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    备注信息
                  </label>
                  <textarea
                    rows={3}
                    placeholder="可填写其他相关信息..."
                    value={formData.notes}
                    onChange={(e) =>
                      setFormData({ ...formData, notes: e.target.value })
                    }
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
                  />
                </div>
              </div>
            </div>
          )}

          {/* 提交按钮 */}
          <button
            onClick={handleSubmit}
            disabled={!selectedEvent || submitting || cycleWarning?.type === 'blocked'}
            className={`w-full py-4 rounded-xl font-medium transition-colors ${
              selectedEvent && !submitting && cycleWarning?.type !== 'blocked'
                ? 'bg-green-700 text-white hover:bg-green-800'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {submitting ? (
              <span className="flex items-center justify-center space-x-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>提交中...</span>
              </span>
            ) : cycleWarning?.type === 'blocked' ? (
              <span className="flex items-center justify-center space-x-2">
                <XCircle className="w-5 h-5" />
                <span>该周期内无法获得积分</span>
              </span>
            ) : (
              <span>确认提交 (+{getEventPoints()}积分)</span>
            )}
          </button>
        </div>
      )}

      {/* 提交成功 */}
      {submitted && (
        <div className="bg-white rounded-2xl shadow-md p-8 text-center">
          <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-6">
            <Check className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">提交成功!</h2>
          <p className="text-gray-600 mb-6">
            事件已提交，等待负责人审核确认
          </p>
          <div className="bg-gray-50 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">用户</span>
              <span className="font-medium text-gray-800">{userInfo.name}</span>
            </div>
            <div className="flex items-center justify-between text-sm mt-2">
              <span className="text-gray-500">事件类型</span>
              <span className="font-medium text-gray-800">
                {eventTypes.find((e) => e.id === selectedEvent)?.name}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm mt-2">
              <span className="text-gray-500">获得积分</span>
              <span className="font-bold text-green-600">+{getEventPoints()}</span>
            </div>
          </div>
          <button
            onClick={handleReset}
            className="w-full py-4 bg-green-700 text-white rounded-xl font-medium hover:bg-green-800 transition-colors"
          >
            继续扫码
          </button>
        </div>
      )}
    </div>
  )
}

export default MedicalScan
